import React from 'react';
import {
  BottomTabBarProps,
  BottomTabNavigationEventMap,
} from '@react-navigation/bottom-tabs';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import {
  NavigationHelpers,
  TabNavigationState,
} from '@react-navigation/native';

import {
  Touchable,
  BaseText,
  Icon,
  TouchableTypeEnum,
} from '@components';
import { ColorType } from '@types';
import { screens } from '../screens';
import { TabParamListType } from '../types';

interface IContainerStyleProps {
  bottomInset: number;
}

const Container = styled(View)((props: IContainerStyleProps) => ({
  flexDirection: 'row',
  height: 52 + props.bottomInset,
  pb: props.bottomInset,
  backgroundColor: '$background0',
  elevation: 3,
  shadowRadius: 16,
  shadowColor: '$shadow',
  shadowOpacity: 0.1,
  shadowOffset: { height: 3, width: 3 },
}));

const TouchableContainer = styled(Touchable)({
  alignItems: 'center',
  flex: 1,
  pt: '$4',
  pb: '$2',
});

function focusedColor(isFocused: boolean): ColorType {
  return isFocused ? '$primary300' : '$heading500';
}

interface ITextStyleProps {
  isFocused: boolean;
}

const Text = styled(BaseText)((props: ITextStyleProps) => ({
  mt: '$2',
  color: focusedColor(props.isFocused),
  variant: 'text.xsmall',
}));

function iconName(route: TabRouteType, isFocused: boolean): string {
  switch (route.name) {
    case screens.HomeTabNavigator:
      return isFocused ? 'home-bold' : 'home-light';
    case screens.MyMusic:
      return isFocused ? 'music-bold' : 'music-light';
    case screens.TrackFeedback:
      return isFocused ? 'music-bold' : 'music-light';
    case screens.Search:
      return isFocused ? 'search-bold' : 'search-light';
    case screens.Hub:
      return isFocused ? 'education-bold' : 'education-light';
    case screens.Profile:
      return isFocused ? 'profile-bold' : 'profile-light';
    default:
      return '';
  }
}

function labelTranslateKey(route: TabRouteType): string {
  switch (route.name) {
    case screens.HomeTabNavigator:
      return 'common.bottomTabBar.home';
    case screens.MyMusic:
      return 'common.bottomTabBar.myMusic';
    case screens.TrackFeedback:
      return 'common.bottomTabBar.trackFeedback';
    case screens.Search:
      return 'common.bottomTabBar.search';
    case screens.Hub:
      return 'common.bottomTabBar.hub';
    case screens.Profile:
      return 'common.bottomTabBar.profile';
    default:
      return '';
  }
}

interface IBottomTabBarProps extends BottomTabBarProps {
  state: TabNavigationState<TabParamListType>;
  navigation: NavigationHelpers<
    TabParamListType,
    BottomTabNavigationEventMap
  >;
}

type TabRouteType = IBottomTabBarProps['state']['routes'][number];

export function BottomTabBar(props: BottomTabBarProps) {
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const { state, navigation, descriptors } =
    props as IBottomTabBarProps;

  return (
    <Container bottomInset={insets.bottom}>
      {state.routes.map((route, index) => {
        const { options } = descriptors[route.key];
        const isFocused = state.index === index;

        function onPress() {
          const event = navigation.emit({
            type: 'tabPress',
            target: route.key,
            canPreventDefault: true,
          });

          if (!isFocused && !event.defaultPrevented) {
            // FIXME: Fix types
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            navigation.navigate(route.name);
          }
        }

        function onLongPress() {
          navigation.emit({
            type: 'tabLongPress',
            target: route.key,
          });
        }

        const accessibilityState = isFocused
          ? { selected: true }
          : {};

        return (
          <TouchableContainer
            type={TouchableTypeEnum.WithoutFeedback}
            key={index}
            role="button"
            accessibilityState={accessibilityState}
            accessibilityLabel={options.tabBarAccessibilityLabel}
            testID={options.tabBarTestID}
            onPress={onPress}
            onLongPress={onLongPress}
          >
            <Icon
              name={iconName(route, isFocused)}
              size={20}
              color={focusedColor(isFocused)}
            />
            <Text isFocused={isFocused}>
              {t(labelTranslateKey(route))}
            </Text>
          </TouchableContainer>
        );
      })}
    </Container>
  );
}
